import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { MemoryVariable } from '@/ai/types/memory-variable.type';
import { ContextData } from '@/ai/types/context-data';

type Memory = Record<string, MemoryVariable | unknown>;

const AndieMemoryContext = createContext<{
    memory: Memory;
    setMemory: (value: Memory) => void;
    getMemoryVariable: <T>(uuid: string) => MemoryVariable<T> | undefined;
    contextData: ContextData[];
    setContextData: (v: ContextData, memory?: Memory) => void;
    clearContextData: (key?: string, memory?: Memory) => void;
} | null>(null);

export function AndieMemoryProvider({ children }: { children: React.ReactNode }) {
    const [memory, setMemoryState] = useState<Memory>({ via: 'frontend' });
    const [contextData, setContextDataState] = useState<ContextData[]>([]);
    const memoryRef = useRef(memory);
    memoryRef.current = memory;

    const changeMemory = useCallback((value: Memory) => {
        const current = memoryRef.current;
        const hasChanges = Object.entries(value).some(
            ([key, val]) => !(key in current) || current[key] !== val,
        );

        if (hasChanges) {
            setMemoryState((prev) => ({ ...prev, ...value }));
        }
    }, []);

    const getMemoryVariable = useCallback(
        function <T>(uuid: string): MemoryVariable<T> | undefined {
            const value = memoryRef.current?.[uuid];

            const isMemoryVariable = (x: unknown): x is MemoryVariable<T> => {
                return x !== null && typeof x === 'object' && 'data' in x;
            };

            return isMemoryVariable(value) ? value : undefined;
        },
        [],
    );

    const setContextData = useCallback(
        (value: ContextData, mem?: Memory) => {
            setContextDataState((previous) => {
                previous = previous.filter((cd) => cd.data.key !== value.data.key);

                const normalized: ContextData = {
                    ...value,
                    display: value.display ?? { type: 'emoji', value: '🧠' },
                    data: {
                        ...value.data,
                        duration: value.data.duration ?? 'temp',
                        placement: value.data.placement ?? 'message',
                    },
                };

                if (normalized.data.placement === 'memory') {
                    changeMemory({
                        [normalized.data.key]: normalized.data.value,
                        ...(mem && { ...mem }),
                    });
                } else if (mem) {
                    changeMemory(mem);
                }

                return [...previous, normalized];
            });
        },
        [changeMemory],
    );

    const clearContextData = useCallback(
        (key?: string, mem?: Memory) => {
            if (key) {
                setContextDataState((previous) => {
                    const found = previous.find((cd) => cd.data.key === key);
                    if (found?.data.placement === 'memory') {
                        changeMemory({
                            [key]: null,
                            ...(mem && { ...mem }),
                        });
                    } else if (mem) {
                        changeMemory(mem);
                    }
                    return previous.filter((cd) => cd.data.key !== key);
                });
            } else {
                setContextDataState([]);
            }
        },
        [changeMemory],
    );

    return (
        <AndieMemoryContext.Provider
            value={{
                memory,
                setMemory: changeMemory,
                getMemoryVariable,
                contextData,
                setContextData,
                clearContextData,
            }}
        >
            {children}
        </AndieMemoryContext.Provider>
    );
}

export function useAndieMemory() {
    const currentContext = useContext(AndieMemoryContext);
    if (!currentContext) {
        throw new Error('useAndieMemory must be used within an AndieMemoryProvider');
    }
    return currentContext;
}
